import {
  DeleteOcrResponse,
  FinalizeOcrRequest,
  FinalizeOcrResponse,
  GetOcrResponse,
  OcrDisplayError,
  OcrItemDto,
  OcrResultDto,
  UploadOcrResponse,
} from '@diamond/shared/types';

type OcrMockScenario = 'success' | 'manual' | 'duplicate' | 'blocked';

interface StoredMockItem {
  item: OcrItemDto;
  scenario: OcrMockScenario;
  pollCount: number;
}

const store = new Map<string, Map<string, StoredMockItem>>();

const SAMPLE_PROOF_SVG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="320" height="320" viewBox="0 0 320 320">
  <rect width="320" height="320" rx="16" fill="#ffffff"/>
  <path d="M42 48h236v224H42z" fill="#f4fbff" stroke="#d7ecfb" stroke-width="4"/>
  <text x="160" y="78" text-anchor="middle" font-family="Arial" font-size="18" font-weight="700" fill="#005bac">BCA</text>
  <circle cx="160" cy="126" r="24" fill="#13b6b1"/>
  <path d="M147 126l9 9 18-20" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
  <text x="160" y="174" text-anchor="middle" font-family="Arial" font-size="14" fill="#263238">Transfer Successful</text>
  <text x="160" y="206" text-anchor="middle" font-family="Arial" font-size="28" font-weight="700" fill="#0000a3">Rp2.000.000</text>
  <text x="66" y="242" font-family="Arial" font-size="11" fill="#4d5658">Beneficiary Name</text>
  <text x="66" y="260" font-family="Arial" font-size="12" font-weight="700" fill="#263238">PT SUKANDA DJAYA</text>
</svg>
`);

const SUCCESS_OCR_RESULT: OcrResultDto = {
  amount: 2000000,
  confidence_score: 0.96,
  recipient_account_number: null,
  recipient_name: 'PT Sukanda Djaya',
  reference_number: 'TRX-20260428-2101',
  sender_account_name: 'Budi Santoso',
  sender_bank: 'BCA',
  transaction_datetime: '2026-04-28T14:01:00.000Z',
  transaction_note: null,
};

const MANUAL_OCR_RESULT: OcrResultDto = {
  ...SUCCESS_OCR_RESULT,
  reference_number: 'TRX-20260428-2102',
  sender_account_name: null,
  sender_bank: null,
};

const DISPLAY_ERRORS: Record<
  Exclude<OcrMockScenario, 'success' | 'manual'>,
  OcrDisplayError
> = {
  duplicate: {
    code: 'DUPLICATE_PAYMENT_PROOF',
    message: 'Bukti transfer sudah pernah digunakan',
  },
  blocked: {
    code: 'RECIPIENT_MISMATCH',
    message: 'Penerima transfer tidak sesuai dengan rekening tujuan pembayaran',
  },
};

function getOrderStore(orderPaymentId: string) {
  const existing = store.get(orderPaymentId);
  if (existing) return existing;

  const next = new Map<string, StoredMockItem>();
  store.set(orderPaymentId, next);
  return next;
}

function createMockId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `00000000-0000-4000-8000-${Math.random()
    .toString(16)
    .slice(2, 14)
    .padEnd(12, '0')}`;
}

function getScenario(file: File, index: number): OcrMockScenario {
  const name = file.name.toLowerCase();
  if (name.includes('duplicate')) return 'duplicate';
  if (name.includes('unreadable') || name.includes('manual')) return 'manual';
  if (name.includes('mismatch') || name.includes('blocked')) return 'blocked';
  return index === 0 ? 'success' : 'manual';
}

function getPreviewUrl(file: File) {
  if (
    typeof URL !== 'undefined' &&
    typeof URL.createObjectURL === 'function' &&
    file.type.startsWith('image/')
  ) {
    return URL.createObjectURL(file);
  }

  return SAMPLE_PROOF_SVG;
}

function buildTerminalItem(stored: StoredMockItem): OcrItemDto {
  if (stored.scenario === 'success') {
    return {
      ...stored.item,
      display_error: null,
      ocr_result: SUCCESS_OCR_RESULT,
    };
  }

  if (stored.scenario === 'manual') {
    return {
      ...stored.item,
      display_error: null,
      ocr_result: MANUAL_OCR_RESULT,
    };
  }

  return {
    ...stored.item,
    display_error: DISPLAY_ERRORS[stored.scenario],
    ocr_result: null,
  };
}

export async function uploadOcrPaymentProofMock(
  orderPaymentId: string,
  files: File[]
): Promise<UploadOcrResponse> {
  const orderStore = getOrderStore(orderPaymentId);
  const items = files.map((file, index) => {
    const id = createMockId();
    const item: OcrItemDto = {
      display_error: null,
      file_name: file.name,
      id,
      ocr_result: null,
      preview_url: getPreviewUrl(file),
    };

    orderStore.set(id, {
      item,
      pollCount: 0,
      scenario: getScenario(file, index),
    });

    return item;
  });

  return { items };
}

export async function getOcrPaymentProofItemsMock(
  orderPaymentId: string
): Promise<GetOcrResponse> {
  const orderStore = getOrderStore(orderPaymentId);

  const items = Array.from(orderStore.values()).map((stored) => {
    const isAnalyzing =
      stored.item.ocr_result === null && stored.item.display_error === null;

    if (isAnalyzing) {
      stored.pollCount += 1;
      if (stored.pollCount >= 2) {
        stored.item = buildTerminalItem(stored);
      }
    }

    return stored.item;
  });

  return { items };
}

export function hasOcrPaymentProofMockItems(orderPaymentId: string): boolean {
  return (store.get(orderPaymentId)?.size ?? 0) > 0;
}

export async function deleteOcrPaymentProofMock(
  id: string
): Promise<DeleteOcrResponse> {
  for (const orderStore of store.values()) {
    if (orderStore.delete(id)) break;
  }

  return { message: 'Deleted' };
}

export async function finalizeOcrPaymentProofMock(
  orderPaymentId: string,
  body: FinalizeOcrRequest
): Promise<FinalizeOcrResponse> {
  const orderStore = getOrderStore(orderPaymentId);
  body.ocr_job_ids.forEach((id) => {
    const stored = orderStore.get(id);
    if (stored) {
      stored.item = buildTerminalItem(stored);
    }
  });

  return { message: 'Payment proof finalized' };
}
